import axios from 'axios';

// Production backend URL - always use this for API calls
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://dactrha.perla-tech.com';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Note: Accept-Language header not added due to CORS restrictions on backend
    // The backend needs to add 'Accept-Language' to Access-Control-Allow-Headers
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't logout/redirect for non-critical endpoints
      // These endpoints may fail with 401 on initial load before auth is ready
      const url = error.config?.url || '';
      const nonCriticalEndpoints = [
        '/update-device-token',
        '/notifications/count',
        '/notifications',
      ];
      
      const isNonCritical = nonCriticalEndpoints.some(endpoint => url.includes(endpoint));
      
      // Check if we have a valid token in localStorage
      // If token exists but we got 401, it might be expired - then logout is appropriate
      // If no token exists, don't force redirect - let the page handle it
      const hasToken = !!localStorage.getItem('token');
      
      if (!isNonCritical && hasToken) {
        // Token exists but got 401 - token is invalid/expired, clear and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Don't redirect if already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      } else if (isNonCritical) {
        console.warn(`Non-critical API ${url} returned 401, ignoring auto-logout...`);
      }
      // If no token and not critical, just let the error propagate - page will handle it
    }
    return Promise.reject(error);
  }
);

export default api;

// ============================================
// Utility Functions
// ============================================
// Helper to get localized value from API response
export const getLocalizedValue = (obj, lang = 'ar') => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj['ar'] || obj['en'] || '';
};

// Transform doctor data from API to our format
export const transformDoctor = (doctor, lang = 'ar') => {
  // Get specialty info - handle both specialties/specialities array and specialty object
  const specialtyData = doctor.specialities?.[0] || doctor.specialties?.[0] || doctor.specialty;
  const specialtyId = specialtyData?.id;
  const specialtyName = getLocalizedValue(specialtyData?.name, lang);
  
  // Store both language versions for search
  const nameEn = typeof doctor.name === 'object' ? (doctor.name.en || '') : '';
  const nameAr = typeof doctor.name === 'object' ? (doctor.name.ar || '') : '';
  const bioEn = typeof doctor.description === 'object' ? (doctor.description.en || '') : '';
  const bioAr = typeof doctor.description === 'object' ? (doctor.description.ar || '') : '';
  
  return {
    id: doctor.id,
    name: getLocalizedValue(doctor.name, lang),
    name_en: nameEn,
    name_ar: nameAr,
    specialty: {
      id: specialtyId,
      name: specialtyName || '',
      name_ar: getLocalizedValue(specialtyData?.name, 'ar'),
      name_en: getLocalizedValue(specialtyData?.name, 'en'),
    },
    rating: doctor.rate || doctor.rating || 0,
    reviews_count: doctor.total_reviews || doctor.reviews_count || 0,
    experience_years: doctor.experience_years || 10,
    address: doctor.clinic ? getLocalizedValue(doctor.clinic.name, lang) : (doctor.address || ''),
    phone: doctor.clinic?.phone_number || doctor.phone || '',
    image: doctor.media?.[0]?.url || doctor.media?.[0]?.thumb || doctor.image,
    bio: getLocalizedValue(doctor.description, lang) || doctor.bio || '',
    bio_en: bioEn,
    bio_ar: bioAr,
    consultation_fee: doctor.price || doctor.consultation_fee || 0,
    available: doctor.available || false,
    city: doctor.city || doctor.clinic?.city || '',
    clinic: doctor.clinic ? {
      id: doctor.clinic.id,
      name: getLocalizedValue(doctor.clinic.name, lang),
      address: doctor.clinic.address_id,
    } : null,
    enable_online_consultation: doctor.enable_online_consultation || false,
  };
};

// Transform specialty data from API
export const transformSpecialty = (specialty, lang = 'ar') => {
  return {
    id: specialty.id,
    name: getLocalizedValue(specialty.name, lang),
    description: getLocalizedValue(specialty.description, lang),
    doctors_count: specialty.doctors_count || 0,
    image: specialty.media?.[0]?.url || specialty.media?.[0]?.thumb,
    color: specialty.color,
  };
};

// ============================================
// Auth Service - Matching Flutter app exactly
// ============================================
export const authService = {
  // Patient Login - POST /api/login (multipart/form-data)
  // Returns: { success: true, data: { user, api_token } } or { success: false, message }
  // Status 450/452: OTP verification required
  // Test credentials: +9630931697454 / Password$123
  loginPatient: (phoneOrEmail, password) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    formData.append('password', password);
    // Add empty fields to match mobile app structure
    formData.append('id', '');
    formData.append('name', '');
    formData.append('email', '');
    formData.append('api_token', '');
    formData.append('phone_number', phoneOrEmail);
    formData.append('address', '');
    formData.append('bio', '');
    return api.post('/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Doctor Login - POST /api/doctor/login (JSON body)
  // Returns: { success: true, data: { user, api_token } } or { success: false, message }
  // Test credentials: test1@gmail.com / Password$123
  loginDoctor: (phoneOrEmail, password) => {
    const payload = {
      id: '',
      name: '',
      email: phoneOrEmail.includes('@') ? phoneOrEmail : '',
      phoneandEmail: phoneOrEmail,
      password: password,
      api_token: null,
      phone_number: phoneOrEmail.includes('@') ? '' : phoneOrEmail,
      address: '',
      bio: '',
      media: [{
        id: '',
        name: null,
        url: `${BACKEND_URL}/images/image_default.png`,
        thumb: `${BACKEND_URL}/images/image_default.png`,
        icon: `${BACKEND_URL}/images/image_default.png`,
        formatted_size: null
      }],
      auth: false,
      roles: []
    };
    return api.post('/doctor/login', payload, {
      headers: { 
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
    });
  },

  // Legacy login method for backward compatibility
  login: (phoneNumber, password, userType = 'patient') => {
    if (userType === 'doctor') {
      return authService.loginDoctor(phoneNumber, password);
    }
    return authService.loginPatient(phoneNumber, password);
  },

  // Register - POST /api/register
  // Returns: { success: true, data: user } or { success: false, message }
  // Status 402: OTP already sent
  register: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone_number', data.phoneNumber);
    formData.append('password', data.password);
    if (data.deviceToken) {
      formData.append('device_token', data.deviceToken);
    }
    return api.post('/register', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Send OTP - POST /api/send-otp
  sendOtp: (phoneOrEmail) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    return api.post('/send-otp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Verify OTP - POST /api/check-otp
  // Returns: { success: true, data: { user, api_token } }
  verifyOtp: (phoneOrEmail, otpCode) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    formData.append('otp_code', otpCode);
    return api.post('/check-otp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get current user profile
  getProfile: () => api.get('/patient/profile'),

  // Update profile
  updateProfile: (data) => api.put('/patient/profile', data),

  // Logout
  logout: () => api.post('/logout'),
};

// ============================================
// Doctor Service
// ============================================
export const doctorService = {
  getAll: (params) => api.get('/doctors', { params: { ...params, with: 'specialities' } }),
  getAllDoctors: (params) => api.get('/all-doctors', { params: { ...params, with: 'specialities' } }),
  getById: (id) => api.get(`/doctors/${id}`, { params: { with: 'specialities' } }),
  getBySpecialty: (specialtyId) => api.get(`/doctors/specialty/${specialtyId}`, { params: { with: 'specialities' } }),
  search: (query) => api.get('/doctors/search', { params: { q: query, with: 'specialities' } }),
  getAvailableSlots: (doctorId, date) => api.get(`/doctors/${doctorId}/slots`, { params: { date } }),
  // Get availability hours from the backend - returns slots with booked status
  // Response format: [[datetime, isAvailable, isBooked], ...]
  getAvailabilityHours: (doctorId, date) => api.get(`/availability_hours/${doctorId}`, { params: { date } }),
  // GET /api/doctor_reviews/{doctor_id} - Get doctor reviews
  getReviews: (doctorId) => api.get(`/doctor_reviews/${doctorId}`),
  // GET /api/galleries?doctor_id={id} - Get doctor gallery
  getGallery: (doctorId) => api.get('/galleries', { params: { doctor_id: doctorId } }),
  // POST /api/doctor_reviews - Add a review for doctor
  addReview: (data) => api.post('/doctor_reviews', data),
};

// ============================================
// Specialty Service
// ============================================
export const specialtyService = {
  getAll: () => api.get('/specialities'),
  getById: (id) => api.get(`/specialities/${id}`),
};

// ============================================
// Appointment Service
// ============================================
export const appointmentService = {
  // Get appointments by status - MUST pass appointment_status_id to avoid backend crash
  // Status IDs: 1=pending/received, 2=accepted, 3=in_clinic, 4=done, 5=cancelled, 6=rejected, 7=waiting, 8=failed/expired
  // Special: -1 = ALL appointments (no status filter)
  getMyAppointments: (statusId = '1') => api.get('/appointments', {
    params: {
      'with': 'appointmentStatus;payment;payment.paymentStatus',
      'search': `appointment_status_id:${statusId}`,
      'orderBy': 'created_at',
      'sortedBy': 'desc',
      'limit': '50',
      'offset': '0'
    }
  }),
  
  // Get all appointments - uses status_id:-1 to fetch all in a SINGLE API call
  // This is much more efficient than making 7 separate calls
  getAllAppointments: async () => {
    try {
      // Try fetching all appointments with status -1 first
      const response = await api.get('/appointments', {
        params: {
          'with': 'appointmentStatus;payment;payment.paymentStatus',
          'search': 'appointment_status_id:-1',  // -1 means ALL statuses
          'orderBy': 'created_at',
          'sortedBy': 'desc',
          'limit': '100',
          'offset': '0'
        }
      });
      
      // If response is successful and has data, return it
      if (response?.data?.data && Array.isArray(response.data.data)) {
        return response;
      }
      
      // Fallback: If -1 doesn't work, fetch common statuses individually
      throw new Error('Fallback to individual status queries');
    } catch (error) {
      console.warn('getAllAppointments with -1 failed, using fallback:', error.message);
      
      // Fallback: Fetch key statuses (pending, confirmed, completed, cancelled) 
      // Use sequential calls instead of parallel to avoid rate limiting
      const appointments = [];
      const statusIds = ['1', '2', '4', '5']; // pending, confirmed, done, cancelled
      
      for (const statusId of statusIds) {
        try {
          const response = await api.get('/appointments', {
            params: {
              'with': 'appointmentStatus;payment;payment.paymentStatus',
              'search': `appointment_status_id:${statusId}`,
              'orderBy': 'created_at',
              'sortedBy': 'desc',
              'limit': '25',
              'offset': '0'
            }
          });
          if (response?.data?.data && Array.isArray(response.data.data)) {
            appointments.push(...response.data.data);
          }
        } catch (err) {
          console.warn(`Failed to fetch status ${statusId}:`, err.message);
        }
      }
      
      return { data: { data: appointments, success: true } };
    }
  },
  
  getById: (id) => api.get(`/appointments/${id}`, {
    params: {
      'with': 'appointmentStatus;user;payment;payment.paymentMethod;payment.paymentStatus'
    }
  }),
  book: (data) => api.post('/appointments', data),
  
  // Cancel appointment - uses PUT /api/appointments/{id} with cancel flag
  // Backend expects: { cancel: '1' } or { appointment_status_id: 5 }
  // This sets payment_status_id to 3 (refunded) and appointment_status_id to 5 (cancelled)
  cancel: (id) => api.put(`/appointments/${id}`, { 
    cancel: '1',
    appointment_status_id: 5 
  }),
  
  // Reschedule appointment - updates the appointment datetime
  // Backend expects: { start_at, ends_at, appointment_at } with timezone
  // Note: Rescheduling is done via standard update endpoint, but status must allow it
  // Allowed transitions: pending(1)->accepted/cancelled, accepted(2)->in_clinic/cancelled
  reschedule: (id, data) => {
    // Build the update payload with new datetime
    const payload = {
      start_at: data.start_at,
      ends_at: data.ends_at,
      appointment_at: data.appointment_at || data.start_at,
      timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return api.put(`/appointments/${id}`, payload);
  },
};

// ============================================
// Medical Record Service - Matching Flutter/Laravel API
// ============================================
export const medicalRecordService = {
  // GET /api/medical-history - Get user's medical history
  getHistory: () => api.get('/medical-history'),
  
  // GET /api/chronic-diseases - Get list of chronic diseases
  getChronicDiseases: () => api.get('/chronic-diseases'),
  
  // GET /api/permanent-medications - Get list of permanent medications
  getMedications: () => api.get('/permanent-medications'),
  getPermanentMedications: () => api.get('/permanent-medications'),
  
  // GET /api/genetic-diseases - Get list of genetic diseases
  getGeneticDiseases: () => api.get('/genetic-diseases'),
  
  // GET /api/surgeries - Get list of surgeries
  getSurgeries: () => api.get('/surgeries'),
  
  // GET /api/vaccines - Get list of vaccines  
  getVaccines: () => api.get('/vaccines'),
  
  // GET /api/all - Get all medical reference data
  getAll: () => api.get('/all'),
  
  // Allergies (may need different endpoint)
  getAllergies: () => api.get('/allergies').catch(() => ({ data: { data: [] } })),
  
  // GET /api/medical-history/{user_id} - Get patient's medical history
  getPatientMedicalRecord: (patientId) => api.get(`/medical-history/${patientId}`),
  
  // POST /api/create-medical-history - Create new medical history
  createMedicalHistory: (data) => api.post('/create-medical-history', data),
  
  // POST /api/medical-history/{id} - Update medical history
  updateMedicalHistory: (id, data) => api.post(`/medical-history/${id}`, data),
  
  // Legacy methods for backward compatibility
  updatePatientMedicalRecord: (patientId, data) => api.post(`/medical-history/${patientId}`, data),
};

// Alias for backward compatibility
export const medicalService = medicalRecordService;

// ============================================
// Patient Service
// ============================================
export const patientService = {
  // Get patients by user ID
  getByUserId: (userId) => api.get('/patients', { 
    params: { 
      search: `user_id:${userId}`,
      searchFields: 'user_id:='
    } 
  }),
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
};

// ============================================
// Notification Service - Matching Flutter/Laravel API
// ============================================
export const notificationService = {
  // GET /api/notifications - Get all notifications
  getAll: () => api.get('/notifications'),
  
  // GET /api/notifications/count - Get unread count
  getCount: () => api.get('/notifications/count'),
  
  // PUT /api/notifications/{id} - Mark single as read
  markAsRead: (id) => api.put(`/notifications/${id}`, { read_at: new Date().toISOString() }),
  
  // DELETE /api/notifications/{id} - Delete notification
  delete: (id) => api.delete(`/notifications/${id}`),
  
  // Bulk mark all as read (custom implementation)
  markAllAsRead: async () => {
    const response = await api.get('/notifications');
    const notifications = response.data?.data || [];
    const unreadIds = notifications.filter(n => !n.read_at).map(n => n.id);
    return Promise.all(unreadIds.map(id => api.put(`/notifications/${id}`, { read_at: new Date().toISOString() })));
  },
};

// ============================================
// Slides/Banner Service
// ============================================
export const slideService = {
  getAll: () => api.get('/slides'),
};

// ============================================
// Wallet Service
// ============================================
export const walletService = {
  getBalance: () => api.get('/wallets'),
  getTransactions: () => api.get('/wallet_transactions'),
  addFunds: (data) => api.post('/wallets/add', data),
};

// ============================================
// Favorites Service
// ============================================
export const favoriteService = {
  getAll: () => api.get('/favorites'),
  add: (doctorId) => api.post('/favorites', { doctor_id: doctorId }),
  remove: (doctorId) => api.delete(`/favorites/${doctorId}`),
  toggle: (doctorId) => api.post('/favorites/toggle', { doctor_id: doctorId }),
};

// ============================================
// Messages/Chat Service
// ============================================
export const messageService = {
  getConversations: () => api.get('/messages'),
  getMessages: (conversationId) => api.get(`/messages/${conversationId}`),
  send: (data) => api.post('/messages', data),
  markAsRead: (conversationId) => api.put(`/messages/${conversationId}/read`),
};

// ============================================
// Clinic Service - Matching Flutter/Laravel API
// ============================================
export const clinicService = {
  // GET /api/clinics - Get all clinics
  getAll: (params) => api.get('/clinics', { params }),
  
  // GET /api/all-clinics - Get all clinics (alternative)
  getAllClinics: (params) => api.get('/all-clinics', { params }),
  
  // GET /api/clinics/{id} - Get single clinic with details
  getById: (id) => api.get(`/clinics/${id}`, { params: { with: 'doctors,specialities' } }),
  
  // GET /api/doctors?clinic_id={id} - Get doctors in clinic
  getDoctors: (clinicId) => api.get('/doctors', { params: { clinic_id: clinicId, with: 'specialities' } }),
  
  // GET /api/clinic_reviews/{id} - Get clinic reviews
  getReviews: (clinicId) => api.get(`/clinic_reviews/${clinicId}`),
  
  // GET /api/galleries?clinic_id={id} - Get clinic gallery
  getGallery: (clinicId) => api.get('/galleries', { params: { clinic_id: clinicId } }),
  
  // POST /api/clinic_reviews - Add a review for clinic
  addReview: (data) => api.post('/clinic_reviews', data),
};

// ============================================
// Pharmacy Service
// ============================================
export const pharmacyService = {
  // Pharmacies
  getAll: (params) => api.get('/pharmacies', { params }),
  getById: (id) => api.get(`/pharmacies/${id}`),
  // Products/Medicines
  getProducts: (params) => api.get('/products', { params }),
  getProductById: (id) => api.get(`/products/${id}`),
  getCategories: () => api.get('/product_categories'),
  // Orders
  getOrders: () => api.get('/orders'),
  getOrderById: (id) => api.get(`/orders/${id}`),
  createOrder: (data) => api.post('/orders', data),
};

// ============================================
// Family Members Service
// ============================================
export const familyService = {
  getAll: () => api.get('/patients', {
    params: {
      search: `user_id:${JSON.parse(localStorage.getItem('user') || '{}').id}`,
      searchFields: 'user_id:='
    }
  }),
  add: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
};

// ============================================
// Content Service (Terms, Privacy, FAQs, Custom Pages)
// ============================================
export const contentService = {
  // Get terms of use - returns HTML content
  getTermsOfUse: () => api.get('/terms-of-use'),
  
  // Get privacy policy - returns HTML content
  getPrivacyPolicy: () => api.get('/user-privacy-policy'),
  
  // Get doctor privacy policy
  getDoctorPrivacyPolicy: () => api.get('/doctor-privacy-policy'),
  
  // Get custom pages (includes About, Contact, etc.)
  getCustomPages: () => api.get('/custom_pages'),
  
  // Get FAQs
  getFaqs: () => api.get('/faqs'),
  
  // Get FAQ categories
  getFaqCategories: () => api.get('/faq_categories'),
  
  // Get app settings (contact info, etc.)
  getSettings: () => api.get('/settings'),
};

// ============================================
// Medicine/Product Service (for MedicineDetailPage)
// ============================================
export const medicineService = {
  // Get all products/medicines
  getAll: (params) => api.get('/products', { params }),
  
  // Get single product/medicine by ID
  getById: (id) => api.get(`/products/${id}`),
  
  // Get product categories
  getCategories: () => api.get('/product_categories'),
  
  // Search products
  search: (query) => api.get('/products', { params: { search: query } }),
};


// ============================================
// Address Service - For user addresses
// ============================================
export const addressService = {
  // GET /api/addresses - Get user's addresses
  getAll: () => api.get('/addresses'),
  
  // GET /api/addresses/{id} - Get single address
  getById: (id) => api.get(`/addresses/${id}`),
  
  // POST /api/addresses - Create new address
  create: (data) => api.post('/addresses', data),
  
  // PUT /api/addresses/{id} - Update address
  update: (id, data) => api.put(`/addresses/${id}`, data),
  
  // DELETE /api/addresses/{id} - Delete address
  delete: (id) => api.delete(`/addresses/${id}`),
};

// ============================================
// FAQ Service - Help page
// ============================================
export const faqService = {
  // GET /api/faqs - Get all FAQs
  getAll: () => api.get('/faqs'),
  
  // GET /api/faq_categories - Get FAQ categories
  getCategories: () => api.get('/faq_categories'),
};
